import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosDecisionService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import { aiBusy } from '@/lib/aiBusy';
import type { CreateDecisionPayload, UpdateDecisionPayload } from '@/types/roros/decisions';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosDecisions(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.decisions.all, params] as const,
    queryFn: () => RorosDecisionService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDecision(id: string) {
  return useQuery({
    queryKey: rorosKeys.decisions.detail(id),
    queryFn: () => RorosDecisionService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDecisionMethods() {
  return useQuery({
    queryKey: rorosKeys.decisions.methods,
    queryFn: () => RorosDecisionService.getMethods(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDecisionLatency() {
  return useQuery({
    queryKey: rorosKeys.decisions.latency,
    queryFn: () => RorosDecisionService.getLatency(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosDecisionCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDecisionPayload) => RorosDecisionService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
    },
  });
}

export function useRorosDecisionUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDecisionPayload }) =>
      RorosDecisionService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosDecisionService.submit(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      RorosDecisionService.approve(id, reason ? { reason } : undefined),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      RorosDecisionService.reject(id, reason ? { reason } : undefined),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionDefer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosDecisionService.defer(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosDecisionService.archive(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

export function useRorosDecisionAdvisoryConsult() {
  const qc = useQueryClient();
  return useMutation({
    // F8 dogfood-fix-wave-1: track advisory consult as AI-busy for wait cursor
    mutationFn: ({ id, personaIds, question }: { id: string; personaIds: string[]; question: string }) => {
      aiBusy.incr();
      return RorosDecisionService.advisoryConsult(id, { persona_ids: personaIds, question })
        .finally(() => aiBusy.decr());
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

/** A.1 EO Connect Demo Phase 2 — Run Decision Moderator and invalidate the decision query. */
export function useRorosDecisionRunModerator() {
  const qc = useQueryClient();
  return useMutation({
    // F8 dogfood-fix-wave-1: track moderator run as AI-busy for wait cursor
    mutationFn: ({ id, context }: { id: string; context?: string }) => {
      aiBusy.incr();
      return RorosDecisionService.runModerator(id, context ? { context } : undefined)
        .finally(() => aiBusy.decr());
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.all });
      qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) });
    },
  });
}

/**
 * Sven dogfood 2026-05-03 — preview the moderator BEFORE persisting.
 * No DB write, no cache invalidation. Used by DecisionFormDialog in
 * CREATE mode. Same global wait-cursor wiring as the persisted runner.
 */
export function useRorosDecisionPreviewModerator() {
  return useMutation({
    mutationFn: (payload: { title: string; description?: string | null; domainId?: string | null }) => {  // A1
      aiBusy.incr();
      return RorosDecisionService.previewModerator(payload)
        .finally(() => aiBusy.decr());
    },
  });
}
